/**
 * Small bridge between the imperative media pipeline and the declarative
 * Preact tree. It owns no markup: it only publishes view state and retains
 * element references that WebCodecs must draw into.
 */
export class PreactRoomUI {
  constructor(updateView) {
    this.updateView = updateView;
    this.tileByParticipant = new Map();
    this.canvasByParticipant = new Map();
    this.unknownFrameChannels = new Set();
    this.localStream = undefined;
    this.localVideoElement = undefined;
  }

  patch(change) {
    this.updateView(current => ({ ...current, ...change }));
  }

  setStatus(text, ok) { this.patch({ status: { text, kind: ok ? "ok" : "err" } }); }
  setMediaNote(mediaNote) { this.patch({ mediaNote }); }
  setCameraEnabled(cameraEnabled) { this.patch({ cameraEnabled }); }
  setCameraButton(capturing) { this.patch({ capturing }); }
  setPreferredVideoSource(preferredVideoSource) { this.patch({ preferredVideoSource }); }
  setVideoPublishing(capturing, activeVideoSource) {
    this.patch({ capturing, activeVideoSource });
  }
  setMicrophoneEnabled(microphoneEnabled) { this.patch({ microphoneEnabled }); }
  setMicrophoneSpeaking(microphoneSpeaking) { this.patch({ microphoneSpeaking }); }
  setAudioDiagnostics(audioDiagnostics) { this.patch({ audioDiagnostics }); }
  setParticipantMedia(participantId, change) {
    this.updateView(current => ({
      ...current,
      participantMedia: {
        ...current.participantMedia,
        [participantId]: { ...current.participantMedia[participantId], ...change }
      }
    }));
  }
  setAudioEnabled(audioEnabled) { this.patch({ audioEnabled }); }
  setParticipantState(participantState) { this.patch({ participantState }); }

  showConference(conference, cameraEnabled) {
    this.patch({ conference, cameraEnabled, inRoom: true, mediaNote: cameraEnabled
      ? "Publishes 360p and 720p camera tracks through FastPubSub."
      : "Your role is listen-only; the authority token has no media publish permission." });
  }

  /**
   * После закрытия хостом — на стартовую страницу встречи
   * (без invite/admin в URL), как /demo/conference.
   */
  exitRoom(message) {
    this.patch({
      inRoom: false,
      conference: undefined,
      room: undefined,
      localParticipantId: undefined,
      cameraEnabled: false,
      capturing: false,
      preferredVideoSource: "camera",
      activeVideoSource: "none",
      audioEnabled: false,
      microphoneEnabled: false,
      microphoneSpeaking: false,
      participantState: {},
      participantMedia: {},
      mediaNote: ""
    });
    this.setStatus(message || "conference closed by its owner", false);
    const start = new URL("/demo/conference", window.location.origin);
    start.searchParams.set("closed", "1");
    window.location.replace(start.href);
  }

  showLinks(created) {
    const invite = new URL("/demo/conference", window.location.origin);
    invite.searchParams.set("invite", created.invite_id);
    const admin = new URL("/demo/conference", window.location.origin);
    admin.searchParams.set("conference", created.conference_id);
    admin.hash = new URLSearchParams({ owner: created.owner_secret }).toString();
    this.patch({ links: { invite: invite.href, admin: admin.href } });
  }

  renderRoom(room, localParticipantId) { this.patch({ room, localParticipantId }); }

  setLocalVideoElement(element) {
    // Preact replaces an inline ref by calling the old callback with null
    // before it calls the new one with the *same* element. Do not forget a
    // live preview during that transient null callback.
    if (!element) {
      if (!this.localStream) this.localVideoElement = undefined;
      return;
    }
    this.localVideoElement = element;
    if (this.localStream) {
      void this.attachLocalStream(element, this.localStream).catch(error => {
        console.warn("[conference] local video preview failed", error);
      });
    }
  }

  setRemoteCanvasElement(participantId, element) {
    if (element) {
      const previous = this.canvasByParticipant.get(participantId);
      if (previous && previous !== element && previous.width > 0) {
        element.width = previous.width;
        element.height = previous.height;
        element.getContext("2d", { alpha: false }).drawImage(previous, 0, 0);
      }
      this.canvasByParticipant.set(participantId, element);
      return;
    }
    const current = this.canvasByParticipant.get(participantId);
    if (current && current.isConnected) this.canvasByParticipant.delete(participantId);
  }

  setParticipantTileElement(participantId, element) {
    if (element) this.tileByParticipant.set(participantId, element);
    else {
      const current = this.tileByParticipant.get(participantId);
      if (current && current.isConnected) this.tileByParticipant.delete(participantId);
    }
  }

  async showLocalStream(stream) {
    this.localStream = stream;
    const video = this.localVideo();
    if (!video) throw new Error("The local video tile is not ready yet.");
    await this.attachLocalStream(video, stream);
    return video;
  }

  async attachLocalStream(video, stream) {
    // Assigning an identical srcObject starts a new media load and aborts a
    // pending play() request. Preact may call this more than once per render.
    if (video.srcObject !== stream) video.srcObject = stream;
    try {
      await video.play();
    } catch (error) {
      // An AbortError only means the browser superseded this particular
      // play() request; the next ref/update will issue one for the live stream.
      if (error?.name !== "AbortError") throw error;
    }
  }

  clearLocalStream() {
    this.localStream = undefined;
    if (this.localVideoElement) {
      this.localVideoElement.pause();
      this.localVideoElement.srcObject = null;
    }
  }

  /** Clears remote participant canvas (last frame must not remain). */
  clearRemoteCanvas(participantId) {
    const canvas = this.canvasByParticipant.get(participantId);
    if (!canvas) return;
    canvas.width = 0;
    canvas.height = 0;
  }

  /**
   * Syncs placeholders with public.*.state: camera off — clear canvas.
   * @param {Record<string, { camera?: boolean, stale?: boolean }>} participantState
   */
  syncParticipantVideoPlaceholders(participantState) {
    for (const [participantId] of this.canvasByParticipant) {
      const state = participantState[participantId];
      if (!state || state.stale || !state.camera) this.clearRemoteCanvas(participantId);
    }
  }

  localVideo() { return this.localVideoElement; }

  /**
   * Canvas and tile for remote video. Creates off-DOM placeholders until Preact
   * mounts the real tile (video may arrive before the first paint).
   */
  remoteVideoTarget(channel) {
    const participantId = participantIdFromChannel(channel);
    if (!participantId) {
      if (!this.unknownFrameChannels.has(channel)) {
        this.unknownFrameChannels.add(channel);
        console.warn("[conference] received video on an unparseable channel", { channel });
      }
      return undefined;
    }
    return this.ensureRemoteTarget(participantId);
  }

  /** Returns canvas + tile refs, creating placeholders when the tile is not mounted yet. */
  ensureRemoteTarget(participantId) {
    let canvas = this.canvasByParticipant.get(participantId);
    if (!canvas) {
      canvas = document.createElement("canvas");
      canvas.className = "remote-video";
      this.canvasByParticipant.set(participantId, canvas);
    }
    let tile = this.tileByParticipant.get(participantId);
    if (!tile) {
      tile = { title: "" };
      this.tileByParticipant.set(participantId, tile);
    }
    return { canvas, tile, participantId };
  }
}

function participantIdFromChannel(channel) {
  const parts = channel.split(".");
  const mediaIndex = parts.indexOf("media");
  // private.<role>.<participant-id>.media.<kind>…
  return mediaIndex >= 1 ? parts[mediaIndex - 1] : undefined;
}
